import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaUserFriends, FaTrash, FaEye, FaPowerOff, FaPlus, FaSearch, FaMapMarkerAlt, FaHome, FaCalendarAlt, FaClock, FaShareAlt } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../pages_premium.css';
import './FlatmatesList.css';
import { getFrontendBaseUrl } from '../utils/url';
import AdminShareModal from '../components/common/AdminShareModal';
import AdminPagination from '../components/common/AdminPagination';

function FlatmatesList() {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [shareItem, setShareItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const handleShareProfile = (profile) => {
        const frontendUrl = getFrontendBaseUrl();
        const userName = profile.fullName || profile.User?.name || 'Roommate Profile';
        setShareItem({
            url: `${frontendUrl}/flatmates/${profile.id}`,
            title: userName,
            category: 'Flatmate Profile',
            price: profile.rent_budget ? `$${profile.rent_budget}/wk` : '',
            location: profile.city || profile.address_or_area || '',
            image: profile.profile_image || profile.User?.avatar || '',
            description: profile.about_me || profile.lifestyle_tags || ''
        });
    };
    const [showModal, setShowModal] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [formData, setFormData] = useState({
        user_id: '', city: '', address_or_area: '', rent_budget: '', 
        looking_for_gender: 'Any', occupation_type: 'Student', 
        lifestyle_tags: '', is_active: true
    });

    const fetchProfiles = async () => {
        try {
            const res = await axiosInstance.get('/flatmates/admin/all');
            setProfiles(res.data);
        } catch (error) {
            console.error('Error fetching flatmate profiles:', error);
            toast.error('Failed to fetch flatmate profiles');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await axiosInstance.get('/admin/users');
            if (Array.isArray(res.data)) {
                setUsersList(res.data);
            } else {
                setUsersList([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to fetch users list');
            setUsersList([]);
        }
    };

    useEffect(() => {
        fetchProfiles();
        fetchUsers();
    }, []);

    const toggleStatus = async (id, currentStatus) => {
        try {
            await axiosInstance.put(`/flatmates/admin/${id}/toggle`);
            toast.success(`Profile is now ${!currentStatus ? 'Active' : 'Inactive'}`);
            fetchProfiles();
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update status');
        }
    };

    const deleteProfile = async (id) => {
        if (!window.confirm('Are you sure you want to delete this flatmate profile?')) return;
        try {
            await axiosInstance.delete(`/flatmates/admin/${id}`);
            toast.success('Profile deleted successfully');
            fetchProfiles();
        } catch (error) {
            console.error('Error deleting profile:', error);
            toast.error('Failed to delete profile');
        }
    };

    const handleCreateProfile = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/flatmates/admin/create', formData);
            toast.success('Profile created successfully');
            setShowModal(false);
            setFormData({
                user_id: '', city: '', address_or_area: '', rent_budget: '', 
                looking_for_gender: 'Any', occupation_type: 'Student', 
                lifestyle_tags: '', is_active: true
            });
            fetchProfiles();
        } catch (error) {
            console.error('Error creating profile:', error);
            toast.error(error.response?.data?.message || 'Failed to create profile');
        }
    };

    const navigate = useNavigate();

    // Helper functions for safe extraction
    const getArrayVal = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) {
                return val.split(',').map(s => s.trim()).filter(Boolean);
            }
        }
        return [];
    };

    const getObjectVal = (val) => {
        if (!val) return {};
        if (typeof val === 'object' && !Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
            } catch (e) {
                return {};
            }
        }
        return {};
    };

    const filtered = profiles.filter(p => {
        const query = search.toLowerCase();
        const userName = p.User?.name?.toLowerCase() || '';
        const userEmail = p.User?.email?.toLowerCase() || '';
        const city = p.city?.toLowerCase() || '';
        const address = p.address_or_area?.toLowerCase() || '';
        const suburbsStr = getArrayVal(p.suburbs).join(' ').toLowerCase();
        const placeTypesStr = getArrayVal(p.place_types).join(' ').toLowerCase();
        return userName.includes(query) || userEmail.includes(query) || city.includes(query) || address.includes(query) || suburbsStr.includes(query) || placeTypesStr.includes(query);
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const paginatedProfiles = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-premium-wrapper flatmates-list-page">
            <div className="page-premium-content">
                
                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaUserFriends size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Flatmate Profiles</h1>
                            <p className="ph-subtitle">Manage all user flatmate listings, preferences & details</p>
                        </div>
                    </div>
                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{profiles.length}</span>
                            <span className="count-lbl">Total Profiles</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span> 
                            All Flatmate Profiles
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="ptable-search">
                                <FaSearch className="ptable-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by user, city, suburb, place type..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <button className="btn-add-primary" onClick={() => setShowModal(true)}>
                                <FaPlus size={12} />
                                <span>Add Profile</span>
                            </button>
                        </div>
                    </div>
                    
                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>User Info</th>
                                    <th>Place Type</th>
                                    <th>Suburbs & City</th>
                                    <th>Budget & Timing</th>
                                    <th>Gender & Occupation</th>
                                    <th>Preferences & Lifestyle</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedProfiles.map((profile, idx) => {
                                    const placeTypes = getArrayVal(profile.place_types);
                                    if (placeTypes.length === 0 && profile.place_type) placeTypes.push(profile.place_type);

                                    const suburbs = getArrayVal(profile.suburbs);
                                    if (suburbs.length === 0 && profile.address_or_area) {
                                        profile.address_or_area.split(',').forEach(s => suburbs.push(s.trim()));
                                    }

                                    const propPrefsObj = getObjectVal(profile.property_preferences);
                                    const propPrefsArr = getArrayVal(profile.property_preferences);
                                    const lifestyleArr = getArrayVal(profile.lifestyle);

                                    return (
                                        <tr key={profile.id}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>{(currentPage - 1) * pageSize + idx + 1}</td>
                                            
                                            {/* User Info */}
                                            <td>
                                                <div className="user-name-cell">
                                                    <div className="user-avatar">
                                                        {profile.User?.name?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                    <div>
                                                        <strong style={{ color: '#0f172a', display: 'block' }}>{profile.User?.name || 'Unknown User'}</strong>
                                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{profile.User?.email}</div>
                                                        {profile.User?.phone && (
                                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>📞 {profile.User?.phone}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Place Types */}
                                            <td>
                                                {placeTypes.length > 0 ? (
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {placeTypes.map((pt, i) => (
                                                            <span key={i} className="badge bg-light text-dark border me-1 mb-1" style={{ fontSize: '11px' }}>
                                                                <FaHome size={10} className="me-1 text-primary" /> {pt}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted" style={{ fontSize: '12px' }}>Any Place</span>
                                                )}
                                            </td>

                                            {/* Suburbs & City */}
                                            <td>
                                                <strong style={{ color: '#0f172a', display: 'block', fontSize: '13px' }}>
                                                    <FaMapMarkerAlt size={12} className="text-danger me-1" />
                                                    {profile.city || 'Australia'}
                                                </strong>
                                                {suburbs.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                        {suburbs.slice(0, 3).map((sub, i) => (
                                                            <span key={i} className="badge bg-secondary bg-opacity-10 text-secondary" style={{ fontSize: '10px' }}>
                                                                {sub}
                                                            </span>
                                                        ))}
                                                        {suburbs.length > 3 && (
                                                            <span className="badge bg-light text-muted" style={{ fontSize: '10px' }}>+{suburbs.length - 3} more</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Budget & Timing */}
                                            <td>
                                                <span className="price-cell mb-1">
                                                    ${profile.rent_budget || 0} <small>/wk</small>
                                                </span>
                                                {profile.move_date && (
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                        <FaCalendarAlt size={10} className="me-1 text-info" /> {profile.move_date}
                                                    </div>
                                                )}
                                                {profile.stay_length && (
                                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                        <FaClock size={10} className="me-1 text-warning" /> {profile.stay_length}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Gender & Occupation */}
                                            <td>
                                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                                                    Pref: <span className="text-primary">{profile.looking_for_gender || 'Any'}</span>
                                                </div>
                                                <span className={`ptag mt-1 ${profile.occupation_type === 'Student' ? 'ptag-student' : 'ptag-admin'}`}>
                                                    {profile.occupation_type || profile.employment_status || 'Student'}
                                                </span>
                                            </td>

                                            {/* Preferences & Lifestyle */}
                                            <td>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {/* Object Preferences */}
                                                    {propPrefsObj.furnishing && (
                                                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25" style={{ fontSize: '10px' }}>
                                                            🛋️ {propPrefsObj.furnishing}
                                                        </span>
                                                    )}
                                                    {propPrefsObj.internet && (
                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '10px' }}>
                                                            🌐 {propPrefsObj.internet}
                                                        </span>
                                                    )}
                                                    {/* Array Preferences */}
                                                    {propPrefsArr.length > 0 && propPrefsArr.map((pref, i) => (
                                                        <span key={i} className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                                                            {pref}
                                                        </span>
                                                    ))}
                                                    {/* Lifestyle Badges */}
                                                    {lifestyleArr.length > 0 && lifestyleArr.slice(0, 2).map((ls, i) => (
                                                        <span key={i} className="badge bg-warning bg-opacity-10 text-dark border border-warning border-opacity-25" style={{ fontSize: '10px' }}>
                                                            ✨ {ls}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td>
                                                {profile.is_active ? (
                                                    <span className="ptag ptag-active">Active</span>
                                                ) : (
                                                    <span className="ptag ptag-banned">Inactive</span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td>
                                                <div className="flat-actions">
                                                    <button
                                                        className="action-btn action-btn-share"
                                                        data-tooltip="Share Profile"
                                                        title="Share Profile"
                                                        onClick={() => handleShareProfile(profile)}
                                                    >
                                                        <FaShareAlt />
                                                    </button>
                                                    <button
                                                        className="action-btn action-btn-view"
                                                        data-tooltip="View Details"
                                                        title="View Details"
                                                        onClick={() => navigate(`/flatmates/${profile.id}`)}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        className="action-btn action-btn-toggle"
                                                        data-tooltip={profile.is_active ? 'Suspend' : 'Activate'}
                                                        title={profile.is_active ? 'Suspend' : 'Activate'}
                                                        onClick={() => toggleStatus(profile.id, profile.is_active)}
                                                    >
                                                        <FaPowerOff />
                                                    </button>
                                                    <button
                                                        className="action-btn action-btn-delete"
                                                        data-tooltip="Delete Profile"
                                                        title="Delete Profile"
                                                        onClick={() => deleteProfile(profile.id)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan="9">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">👥</div>
                                                <p>No flatmate profiles found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="profiles"
                    />
                </div>
            </div>

            {/* Create Profile Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontWeight: 700, fontSize: '1.1rem' }}>Create Flatmate Profile</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateProfile}>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-md-12 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>Select User <span className="text-danger">*</span></Form.Label>
                                    <Form.Control as="select" required 
                                        value={formData.user_id} 
                                        onChange={(e) => setFormData({...formData, user_id: e.target.value})}>
                                        <option value="">-- Choose User --</option>
                                        {usersList.map(u => (
                                            <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>City <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" required placeholder="e.g. Sydney"
                                        value={formData.city} 
                                        onChange={(e) => setFormData({...formData, city: e.target.value})} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>Area / Suburbs</Form.Label>
                                    <Form.Control type="text" placeholder="e.g. Parramatta, Ultimo"
                                        value={formData.address_or_area} 
                                        onChange={(e) => setFormData({...formData, address_or_area: e.target.value})} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>Rent Budget ($/wk) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="number" required placeholder="250"
                                        value={formData.rent_budget} 
                                        onChange={(e) => setFormData({...formData, rent_budget: e.target.value})} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>Gender Preference</Form.Label>
                                    <Form.Control as="select" 
                                        value={formData.looking_for_gender} 
                                        onChange={(e) => setFormData({...formData, looking_for_gender: e.target.value})}>
                                        <option value="Any">Any</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>Occupation Type</Form.Label>
                                    <Form.Control as="select" 
                                        value={formData.occupation_type} 
                                        onChange={(e) => setFormData({...formData, occupation_type: e.target.value})}>
                                        <option value="Student">Student</option>
                                        <option value="Working Professional">Working Professional</option>
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 700 }}>Lifestyle Tags (comma separated)</Form.Label>
                                    <Form.Control type="text" placeholder="e.g. Non-smoker, Early Bird"
                                        value={formData.lifestyle_tags} 
                                        onChange={(e) => setFormData({...formData, lifestyle_tags: e.target.value})} />
                                </Form.Group>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                        <Button style={{ background: 'linear-gradient(90deg, #F1191E 0%, #FA5417 50%, #FF7409 100%)', border: 'none' }} type="submit">Create Profile</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <AdminShareModal
                isOpen={!!shareItem}
                onClose={() => setShareItem(null)}
                {...shareItem}
            />
        </div>
    );
}

export default FlatmatesList;
